import {useMemo, useState} from 'react';
import {ArchiveBoxIcon} from '@heroicons/react/24/outline';
import {
    createTable,
    SortingState as TableSortingState,
    TableHeaderSpec,
} from '../../../../../components/table/CreateTable';
import {cleanDocumentFileName} from '../../../../../lib/TFICLib';
import {formatDate} from '../../../../../lib/utils.ts';
import {
    ArchivedDocument,
    ArchivedDocumentsSorting,
} from './archivedDocuments.ts';
import type {ArchivedDocumentsOrderByEnum} from '../../../../../generated/enums.ts';

const SORT_COLUMNS: Record<ArchivedDocumentsOrderByEnum, true> = {
    created_at: true,
    file_name: true,
    business_name: true,
};

function isSortColumn(field: string): field is ArchivedDocumentsOrderByEnum {
    return Object.prototype.hasOwnProperty.call(SORT_COLUMNS, field);
}

type ArchivedDocumentsTableProps = {
    documents: ArchivedDocument[];
    isLoading: boolean;
    sorting: ArchivedDocumentsSorting;
    onSortingChange: (sorting: ArchivedDocumentsSorting) => void;
    onRowClick: (documentId: string, businessId: string) => void;
};

export function ArchivedDocumentsTable({
    documents,
    isLoading,
    sorting,
    onSortingChange,
    onRowClick,
}: ArchivedDocumentsTableProps) {
    const [currentSortingState, setCurrentSortingState] = useState<TableSortingState | null>({
        orderBy: sorting.orderby,
        orderDescending: sorting.order === 'desc',
    });

    const tableSpec = useMemo<TableHeaderSpec<ArchivedDocument>>(() => ({
        title: documents.length === 1 ? '1 document' : `${documents.length} documenten`,
        isLoading,
        options: {
            disableTopPaginationWidget: false,
            disableBottomPaginationWidget: true,
            allowFullRowClick: true,
        },
        onRowClick: (row) => onRowClick(row.id, row.business_id),
        sorting: {
            sortingState: [currentSortingState, setCurrentSortingState],
            inMemory: false,
        },
        onQueryUpdate: (_pagination, updatedSorting) => {
            onSortingChange({
                orderby: updatedSorting?.orderBy && isSortColumn(updatedSorting.orderBy) ? updatedSorting.orderBy : 'created_at',
                order: updatedSorting?.orderDescending === false ? 'asc' : 'desc',
            });
        },
        emptyRowMessage: 'Geen gearchiveerde documenten gevonden',
        columns: [
            {
                name: 'Document',
                field: 'file_name',
                style: {widthMultiplier: 2, skeleton: 'lead'},
                customRender: (document: ArchivedDocument) => (
                    <div className="flex items-center gap-4">
                        <ArchiveBoxIcon className="h-8 w-8 shrink-0 text-gray-400" aria-hidden="true"/>
                        <span className="truncate text-sm text-gray-900">{cleanDocumentFileName(document.file_name)}</span>
                    </div>
                ),
            },
            {
                name: 'Klant',
                field: 'business_name',
                customRender: (document: ArchivedDocument) => (
                    <span className="text-sm text-gray-700">{document.business_name}</span>
                ),
            },
            {
                name: 'Uploaddatum',
                field: 'created_at',
                style: {widthMultiplier: 0.6},
                customRender: (document: ArchivedDocument) => (
                    <span className="text-sm text-gray-700">{formatDate(document.created_at)}</span>
                ),
            },
        ],
    }), [documents.length, isLoading, currentSortingState, onRowClick, onSortingChange]);

    const TableComponent = createTable<ArchivedDocument>(documents, tableSpec);

    return TableComponent();
}
